import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SearchDialog from "@/components/feature/SearchDialog";
import FavoritesDrawer from "@/components/feature/FavoritesDrawer";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电视剧", to: "/category/drama" },
  { label: "电影", to: "/category/movie" },
  { label: "网剧", to: "/category/web" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const { favorites } = useFavorites();

  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  const handleNavClick = () => setMenuOpen(false);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          transparent
            ? "bg-transparent"
            : "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-content items-center gap-4 px-4 md:h-[72px] md:px-6">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5 cursor-pointer"
            onClick={handleNavClick}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 dark:text-foreground-950">
              <i className="ri-movie-2-fill text-lg"></i>
            </span>
            <span
              className={`font-heading text-lg font-bold tracking-wide transition-colors md:text-xl ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              甜蜜的惩罚
            </span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? transparent
                      ? "bg-background-50/20 text-background-50"
                      : "bg-primary-50 text-primary-600"
                    : transparent
                      ? "text-background-50/85 hover:text-background-50"
                      : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="搜索"
              className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-colors cursor-pointer ${
                transparent
                  ? "text-background-50 hover:bg-background-50/20"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-search-line"></i>
            </button>
            <button
              type="button"
              onClick={() => setFavOpen(true)}
              aria-label="我的收藏"
              className={`relative flex h-10 w-10 items-center justify-center rounded-full text-lg transition-colors cursor-pointer ${
                transparent
                  ? "text-background-50 hover:bg-background-50/20"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-heart-3-line"></i>
              {favorites.length > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50 dark:text-foreground-950">
                  {favorites.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="菜单"
              className={`flex h-10 w-10 items-center justify-center rounded-full text-xl transition-colors lg:hidden cursor-pointer ${
                transparent
                  ? "text-background-50 hover:bg-background-50/20"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="animate-slide-down border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={handleNavClick}
                  className={`rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.to)
                      ? "bg-primary-50 text-primary-600"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <FavoritesDrawer open={favOpen} onClose={() => setFavOpen(false)} />
    </>
  );
}